// Массовое обновление прошивки: один образ на выбранные панели. Каждая
// панель — отдельный adminStartFirmwareUpdate по очереди, как в окне одной
// панели: журнал Sales пишет каждую, отказ Шанырака по одной панели не
// останавливает остальные. Подходит ли образ — те же правила, что в окне
// одной панели; окончательно решает Шанырак.

import { useEffect, useMemo, useState } from "react";
import { gql } from "../lib/gql";
import {
  FIRMWARE_IMAGES_QUERY,
  PANEL_FIRMWARE_QUERY,
  PHASE_LABEL,
  START_UPDATE_MUTATION,
  failureText,
  hwRange,
  imageMismatch,
  phaseActive,
  type FirmwareImage,
  type FirmwareResult,
  type PanelFirmware,
} from "../lib/firmware";

// adminPanelFirmware принимает не больше 200 панелей за запрос.
const FIRMWARE_CHUNK = 200;

type Outcome = { ok: boolean; text: string };

// Почему панель пропускается с этим образом; null — обновление можно
// запускать.
function skipReason(img: FirmwareImage, fw: PanelFirmware | undefined): string | null {
  if (!fw) return "не принимает обновление прошивки или Шанырак её не знает";
  if (fw.update && phaseActive(fw.update.phase)) {
    return `уже обновляется до ${fw.update.target.version} (${PHASE_LABEL[fw.update.phase]})`;
  }
  return imageMismatch(img, fw);
}

export function BulkFirmwareModal({
  deviceIds,
  labels,
  onClose,
  onChanged,
}: {
  deviceIds: string[];
  labels: Map<string, string>;
  onClose: () => void;
  onChanged: (fw: PanelFirmware) => void;
}) {
  const [images, setImages] = useState<FirmwareImage[]>([]);
  const [firmwares, setFirmwares] = useState<Record<string, PanelFirmware>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageId, setImageId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});

  useEffect(() => {
    let cancelled = false;
    const chunks: string[][] = [];
    for (let i = 0; i < deviceIds.length; i += FIRMWARE_CHUNK) chunks.push(deviceIds.slice(i, i + FIRMWARE_CHUNK));
    Promise.all([
      gql<{ adminFirmwareImages: FirmwareImage[] }>(FIRMWARE_IMAGES_QUERY),
      ...chunks.map((ids) => gql<{ adminPanelFirmware: PanelFirmware[] }>(PANEL_FIRMWARE_QUERY, { deviceIds: ids })),
    ])
      .then(([imgs, ...parts]) => {
        if (cancelled) return;
        setImages((imgs as { adminFirmwareImages: FirmwareImage[] }).adminFirmwareImages);
        const byId: Record<string, PanelFirmware> = {};
        for (const p of parts as Array<{ adminPanelFirmware: PanelFirmware[] }>) {
          for (const fw of p.adminPanelFirmware) byId[fw.deviceId] = fw;
        }
        setFirmwares(byId);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceIds]);

  const image = images.find((i) => i.id === imageId) ?? null;
  const plan = useMemo(
    () => deviceIds.map((id) => ({ id, skip: image ? skipReason(image, firmwares[id]) : null })),
    [deviceIds, image, firmwares],
  );
  const eligible = image ? plan.filter((p) => p.skip === null && !outcomes[p.id]?.ok) : [];
  const done = Object.keys(outcomes).length > 0;

  async function start() {
    if (!image) return;
    setConfirming(false);
    setRunning(true);
    for (const { id } of eligible) {
      let outcome: Outcome;
      try {
        const d = await gql<{ adminStartFirmwareUpdate: FirmwareResult & { firmware: PanelFirmware | null } }>(
          START_UPDATE_MUTATION,
          { deviceId: id, imageId: image.id },
        );
        const r = d.adminStartFirmwareUpdate;
        if (r.firmware) {
          setFirmwares((prev) => ({ ...prev, [id]: r.firmware! }));
          onChanged(r.firmware);
        }
        outcome = r.success ? { ok: true, text: "запущено" } : { ok: false, text: failureText(r) };
      } catch (err) {
        outcome = { ok: false, text: err instanceof Error ? err.message : String(err) };
      }
      setOutcomes((prev) => ({ ...prev, [id]: outcome }));
    }
    setRunning(false);
  }

  const started = Object.values(outcomes).filter((o) => o.ok).length;
  const failed = Object.values(outcomes).filter((o) => !o.ok).length;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center", zIndex: 100 }}
      onClick={running ? undefined : onClose}
    >
      <div className="card" style={{ maxWidth: 720, width: "92%", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 12px 0" }}>Обновить прошивку: {deviceIds.length} устройств</h3>

        {loading ? (
          <div className="muted">Загружаем…</div>
        ) : loadError ? (
          <div className="error-banner">{loadError}</div>
        ) : (
          <>
            <label style={{ fontSize: 13 }}>Образ</label>
            <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
              <select
                value={imageId}
                onChange={(e) => { setImageId(e.target.value); setConfirming(false); }}
                disabled={running}
              >
                <option value="">— выберите образ —</option>
                {images.map((img) => (
                  <option key={img.id} value={img.id}>
                    {img.version} · {img.kind === "DEV" ? "dev" : "выпуск"} · hw {hwRange(img)}
                  </option>
                ))}
              </select>
              {image && !confirming && (
                <button type="button" disabled={running || eligible.length === 0} onClick={() => setConfirming(true)}>
                  Запустить на {eligible.length}
                </button>
              )}
              {image && confirming && (
                <>
                  <button type="button" disabled={running} onClick={() => void start()}>
                    Да, обновить {eligible.length} до {image.version}
                  </button>
                  <button type="button" className="secondary" disabled={running} onClick={() => setConfirming(false)}>
                    Отмена
                  </button>
                </>
              )}
            </div>

            {done && (
              <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                {running ? "Запускаем… " : ""}Запущено: {started}{failed > 0 ? `, отказ: ${failed}` : ""}. Команда
                уйдёт каждой панели, когда она заберёт свежие настройки; ход — в колонке «Прошивка».
              </div>
            )}

            {image && (
              <table>
                <thead>
                  <tr>
                    <th>Панель</th>
                    <th>Сейчас</th>
                    <th>Итог</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.map(({ id, skip }) => {
                    const fw = firmwares[id];
                    const outcome = outcomes[id];
                    return (
                      <tr key={id}>
                        <td style={{ fontFamily: "monospace" }}>{labels.get(id) ?? id.slice(0, 8)}</td>
                        <td style={{ fontFamily: "monospace" }}>{fw?.swVersion ?? "—"}</td>
                        <td style={{ fontSize: 13 }}>
                          {outcome ? (
                            <span style={{ color: outcome.ok ? "var(--success)" : "var(--danger)" }}>{outcome.text}</span>
                          ) : skip ? (
                            <span className="muted">пропуск — {skip}</span>
                          ) : (
                            <span>будет обновлена</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        <div className="row" style={{ justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" className="secondary" disabled={running} onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
